// UPlotGraph.jsx
import styles from '../Simulation.module.css';

import React from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';

function findNearestIdx(xData, x) {
    if (!xData || xData.length === 0) return null;
    let lo = 0,
        hi = xData.length - 1;
    if (x <= xData[lo]) return lo;
    if (x >= xData[hi]) return hi;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (xData[mid] < x) lo = mid;
        else hi = mid;
    }
    return x - xData[lo] <= xData[hi] - x ? lo : hi;
}

// --- 커스텀 레전드 플러그인(우상단) ---
function customLegendPlugin({ unit = '', compact = false, fmt = (v) => (v == null ? '-' : String(v)), getPlayhead = () => null } = {}) {
    let legendEl;
    let nowValEl, nowTimeEl, swatch;

    return {
        hooks: {
            init: (u) => {
                const host = u.root;
                host.style.position = host.style.position || 'relative';

                legendEl = document.createElement('div');
                legendEl.className = 'u-legend-custom';
                Object.assign(legendEl.style, {
                    position: 'absolute',
                    top: compact ? '-25px' : '-20px',
                    right: compact ? '10px' : '30px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    background: 'rgba(255,255,255,0.85)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    fontSize: '12px',
                });

                const rowNow = document.createElement('div');
                Object.assign(rowNow.style, { display: 'flex', alignItems: 'center', gap: '6px' });

                swatch = document.createElement('span');
                swatch.className = 'u-legend-swatch';
                Object.assign(swatch.style, { width: '10px', height: '10px', borderRadius: '2px', display: 'inline-block' });
                // stroke가 함수/undefined일 수 있으니 방어
                let stroke = u.series?.[1]?.stroke;
                try {
                    stroke = typeof stroke === 'function' ? stroke(u, 1) : stroke;
                } catch {}
                swatch.style.background = stroke || '#2563eb';

                const labelNow = document.createElement('span');
                labelNow.textContent = 'Now:';
                Object.assign(labelNow.style, { color: '#0f172a', fontWeight: 600 });

                nowTimeEl = document.createElement('span');
                Object.assign(nowTimeEl.style, { color: '#0f172a', fontWeight: 600 });

                nowValEl = document.createElement('span');
                nowValEl.style.fontWeight = 600;

                rowNow.append(swatch, labelNow, nowTimeEl, nowValEl);
                legendEl.append(rowNow);
                host.appendChild(legendEl);

                nowValEl.textContent = '-';
                nowTimeEl.textContent = 't: -';
            },
            draw: (u) => {
                // Now(플레이헤드) 값 갱신
                const xData = u.data[0],
                    yData = u.data[1];
                const tNow = getPlayhead ? getPlayhead() : null; // 초 단위
                if (tNow == null || !Array.isArray(xData)) {
                    nowTimeEl.textContent = 't: -';
                    nowValEl.textContent = '-';
                    return;
                }
                const idx = findNearestIdx(xData, tNow);
                nowTimeEl.textContent = `t: ${Math.floor(tNow)}s`;
                const v = idx != null ? yData[idx] : null;
                nowValEl.textContent = v == null ? '-' : `${fmt(v)}${unit ? ' ' + unit : ''}`;
            },
        },
    };
}

function tooltipPlugin({ unit = '', fmt = (v) => (v == null ? '-' : String(v)) } = {}) {
    let tip;
    return {
        hooks: {
            init: (u) => {
                const host = u.root;
                tip = document.createElement('div');
                Object.assign(tip.style, {
                    position: 'absolute',
                    pointerEvents: 'none',
                    transform: 'translate(-50%, -120%)',
                    padding: '4px 6px',
                    fontSize: '12px',
                    borderRadius: '6px',
                    background: '#0f172a',
                    color: '#fff',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 6px rgba(0,0,0,.3)',
                    display: 'none',
                });
                host.appendChild(tip);
            },
            setCursor: (u) => {
                const { left, top, idx } = u.cursor;
                if (idx == null || idx < 0) {
                    tip.style.display = 'none';
                    return;
                }
                const xData = u.data[0],
                    yData = u.data[1];
                const t = xData[idx],
                    v = yData[idx];
                tip.textContent = `t=${Math.floor(t)}s, ${fmt(v)}${unit ? ' ' + unit : ''}`;
                tip.style.left = left + 'px';
                tip.style.top = top + 'px';
                tip.style.display = 'block';
            },
            setSeries: () => {},
        },
    };
}

// 보조선 플러그인(그대로)
function refLinesPlugin(getX, getY) {
    return {
        hooks: {
            draw: (u) => {
                const ctx = u.ctx;
                const xVal = getX ? getX() : null;
                if (xVal != null) {
                    const x = Math.round(u.valToPos(xVal, 'x', true));
                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(x + 0.5, Math.floor(u.bbox.top));
                    ctx.lineTo(x + 0.5, Math.floor(u.bbox.top + u.bbox.height));
                    ctx.lineWidth = 1;
                    ctx.setLineDash([6, 6]);
                    ctx.strokeStyle = '#004cffff';
                    ctx.stroke();
                    ctx.restore();
                }
                const yVal = getY ? getY() : null;
                if (yVal != null) {
                    const y = Math.round(u.valToPos(yVal, 'y', true));
                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(Math.floor(u.bbox.left), y + 0.5);
                    ctx.lineTo(Math.floor(u.bbox.left + u.bbox.width), y + 0.5);
                    ctx.lineWidth = 2;
                    ctx.setLineDash([6, 6]);
                    ctx.strokeStyle = '#ff0000ff';
                    ctx.stroke();
                    ctx.restore();
                }
            },
        },
    };
}

function UPlotGraph({ title, x = [], y = [], color = '#2563eb', yUnit = '', time, threshold, height, compact = false, showTitle = true }) {
    const hostRef = React.useRef(null);
    const plotRef = React.useRef(null);
    const roRef = React.useRef(null);
    const rafRef = React.useRef(0);
    // 리사이즈 시 재시도 트리거
    const [sizeTick, setSizeTick] = React.useState(0);

    const data = React.useMemo(() => [x, y], [x, y]);

    // y축 범위 계산
    const { yMin, yMax } = React.useMemo(() => {
        const hasData = Array.isArray(y) && y.length > 0;
        const yMinData = hasData ? Math.min(...y) : 0;
        const yMaxData = hasData ? Math.max(...y) : 0;
        const baseMin = Math.min(0, Number.isFinite(yMinData) ? yMinData : 0);

        if (Number.isFinite(threshold) && yMaxData < threshold) {
            return { yMin: baseMin, yMax: threshold };
        }
        const span = Math.max(1e-9, yMaxData - baseMin);
        return { yMin: baseMin, yMax: yMaxData + span * 0.05 };
    }, [y, threshold]);

    // 준비 체크
    const readyToInit = React.useMemo(() => {
        const el = hostRef.current;
        const w = el?.clientWidth || 0;
        const h = height == null ? el?.clientHeight || 0 : height;
        const hasSize = true; //w > 0 && h > 0;
        const hasData = Array.isArray(x) && x.length > 0 && Array.isArray(y) && y.length > 0;
        return hasSize && hasData;
    }, [x, y, height, sizeTick]);

    // 인스턴스 생성 (준비되면 1회)
    React.useLayoutEffect(() => {
        if (plotRef.current) return; // 이미 만들어진 경우
        if (!readyToInit) return; // 아직 준비 안됨

        const el = hostRef.current;
        if (!el) return;

        const width = el.clientWidth || 300;
        const initH = height == null ? el.clientHeight || 180 : height;

        const opts = {
            width,
            height: initH,
            tzDate: (ts) => new Date(ts),
            scales: { x: { time: false }, y: { auto: false, range: [yMin, yMax] } },
            padding: compact ? [14, 8, 0, 0] : [12, 16, 0, 0],
            axes: [
                {
                    stroke: '#475569',
                    grid: { show: true, stroke: '#e2e8f0' },
                    // x 값이 실수여도 잘 보이도록 깔끔한 라벨만 남김
                    values: (u, vals) =>
                        vals.map((v) => {
                            // 정수 초일 때만 표기 (필요하면 커스터마이즈)
                            return Math.abs(v - Math.round(v)) < 1e-9 ? String(Math.round(v)) : '';
                        }),
                    label: '시간 (s)',
                    size: 28,
                },
                {
                    stroke: '#475569',
                    grid: { show: true, stroke: '#e2e8f0' },
                    label: showTitle ? yUnit : '',
                    size: compact ? 20 : 48,
                },
            ],
            series: [{}, { label: title ?? '', stroke: color, width: 2, points: { show: false } }],
            legend: { show: false },
            plugins: [
                refLinesPlugin(
                    () => (Number.isFinite(time) ? Math.floor(time) : null),
                    () => threshold ?? null
                ),
                customLegendPlugin({
                    unit: yUnit,
                    fmt: (v) => (Number.isFinite(v) ? v.toFixed(1) : '-'),
                    getPlayhead: () => (Number.isFinite(time) ? time : null),
                    compact,
                }),
                tooltipPlugin({ unit: yUnit, fmt: (v) => (Number.isFinite(v) ? v.toFixed(1) : '-') }),
            ],
        };

        const plot = new uPlot(opts, data, el);
        plotRef.current = plot;

        // ResizeObserver: 사이즈 변화는 RAF에서 setSize
        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(() => {
                if (rafRef.current) cancelAnimationFrame(rafRef.current);
                rafRef.current = requestAnimationFrame(() => {
                    rafRef.current = 0;
                    const W = el.clientWidth || 0;
                    const H = height == null ? el.clientHeight || 180 : height;
                    if (W && H) plot.setSize({ width: W, height: H });
                });
            });
            ro.observe(el);
            roRef.current = ro;
        }

        return () => {
            roRef.current?.disconnect?.();
            roRef.current = null;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            plotRef.current?.destroy();
            plotRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readyToInit]);

    // 컨테이너가 0폭에서 나중에 레이아웃 잡히는 케이스를 위해 sizeTick 갱신
    React.useEffect(() => {
        const el = hostRef.current;
        if (!el) return;

        // 첫 마운트 직후 한 번 더 체크 (레이아웃 settle)
        const id = requestAnimationFrame(() => setSizeTick((t) => t + 1));
        return () => cancelAnimationFrame(id);
    }, []);

    // 데이터 갱신 (이미 만들어졌다면 setData)
    React.useEffect(() => {
        const plot = plotRef.current;
        if (!plot) return;

        const hasData = Array.isArray(x) && x.length > 0 && Array.isArray(y) && y.length > 0;
        if (!hasData) return;

        plot.setData([x, y], true);
    }, [x, y]);

    // y축 범위 갱신
    React.useEffect(() => {
        const plot = plotRef.current;
        if (!plot) return;
        plot.setScale('y', { min: yMin, max: yMax });
    }, [yMin, yMax]);

    // 커서/임계선 재그리기
    React.useEffect(() => {
        plotRef.current?.redraw();
    }, [time, threshold]);

    return (
        <div className={styles.chartPane}>
            {title && <div className={styles.chartTitle}>{title}</div>}
            <div
                className={styles.chartHost}
                ref={hostRef}
                style={{
                    width: '100%',
                    height: height == null ? '100%' : `${height}px`,
                    position: 'relative',
                }}
            />
        </div>
    );
}

export default UPlotGraph;
